import $ from 'jquery';
import _find from 'lodash/find';
import _findIndex from 'lodash/findIndex';
import _without from 'lodash/without';

import {
  fetchComments,
  updateAddCommentError,
  updateAddReplyError,
  updateAddReplyId,
  updateComments,
  updateExpandedReplyIds,
  updateFetchingCommentsError,
  updateIsFetching,
} from 'actions/comments';
import { showErrorBar } from 'actions/errorBar';
import { ERROR } from 'constants/error-severities';
import {
  VIDEO_COMMENT_CREATED,
  VIDEO_COMMENT_REPLY_CREATED,
} from 'constants/events';
import { HEADER_CONTENT_TYPE } from 'constants/http';
import { JSON as JSON_MIME } from 'constants/mimes';
import * as analytics from 'utilities/analytics';
import * as loggerx from 'utilities/loggerx';

import { getVideoURL } from '../../../shared/utilities/url';

const SERVER_ERROR = 'serverError';

export function addCommentCreator(content, medium, mentions) {
  return (dispatch, getState) => {
    dispatch(updateIsFetching(true));

    const { comments: commentsState, share_video } = getState();
    const {
      video: { id },
      password,
    } = share_video;
    const { anon_user_name, comments, time_stamp } = commentsState;
    const urlTimeSuffix = time_stamp > 0 ? `?t=${time_stamp}s` : '';

    const payload = {
      content,
      password,
      time_stamp,
      user_name: anon_user_name.length > 0 ? anon_user_name : null,
      video_id: id,
      mentions: {
        text: `${$('.add-comment-content')[0].innerText} ${getVideoURL(
          id
        )}${urlTimeSuffix}`,
        mentions,
      },
    };

    // Tell server that user chose a name, so reactions, etc. are updated
    fetch('/v1/auth/anon_username', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body: JSON.stringify({ user_name: anon_user_name }),
    });

    return fetch('/v1/comments/post', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error adding comment',
          };
        }

        return resp.json();
      })
      .then(commentJSON => {
        medium.value('');

        const sortedComments = comments.concat(commentJSON).sort((a, b) => {
          return new Date(a.createdAt) - new Date(b.createdAt);
        });

        dispatch(updateComments(sortedComments));
        analytics.track(VIDEO_COMMENT_CREATED, {
          comment_id: commentJSON.id,
          video_id: id,
          mentions: Object.keys(mentions).length,
        });
        dispatch(updateIsFetching(false));
      })
      .catch(err => {
        dispatch(updateAddCommentError(err.message.substring(0, 12)));
        dispatch(showErrorBar(ERROR, err.message));
        dispatch(updateIsFetching(false));

        if (err.type === SERVER_ERROR) {
          return;
        }

        loggerx.error(
          err,
          { videoId: id, name: payload.user_name },
          { team: 'create' }
        );
      });
  };
}

export function addReplyCreator(content, commentId, mentions) {
  return (dispatch, getState) => {
    const { comments: commentsState, share_video } = getState();
    const {
      video: { id: videoId },
      password,
    } = share_video;
    const { anon_user_name, comments, time_stamp } = commentsState;

    const payload = {
      content,
      password,
      time_stamp,
      user_name: anon_user_name.length > 0 ? anon_user_name : null,
      comment_post_id: commentId,
      video_id: videoId,
      mentions: {
        text: `${$('.add-reply-content')[0].innerText} ${getVideoURL(videoId)}`,
        mentions,
      },
    };

    return fetch('/v1/comments/reply', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error adding reply',
          };
        }

        return resp.json();
      })
      .then(replyJSON => {
        const parentComment = {
          ..._find(comments, { id: replyJSON.comment_post_id }),
        };

        const parentCommentIndex = _findIndex(comments, {
          id: parentComment.id,
        });
        const newCommentsArr = comments.slice(0);

        parentComment.replies = (parentComment.replies || []).concat({
          ...replyJSON,
          force_show: true,
        });

        newCommentsArr.splice(parentCommentIndex, 1, parentComment);
        dispatch(updateComments(newCommentsArr));
        dispatch(updateAddReplyId(null));
        analytics.track(VIDEO_COMMENT_REPLY_CREATED, {
          comment_id: commentId,
          video_id: videoId,
          mentions: Object.keys(mentions).length,
        });
      })
      .catch(err => {
        dispatch(updateAddReplyError(err.message.substring(0, 12)));
        dispatch(showErrorBar(ERROR, err.message));

        if (err.type === SERVER_ERROR) {
          return;
        }

        loggerx.error(err, { videoId, commentId }, { team: 'create' });
      });
  };
}

export function deleteCommentCreator(id) {
  return (dispatch, getState) => {
    dispatch(updateIsFetching(true));

    const { password } = getState().share_video;
    const { comments } = getState().comments;

    return fetch(`/v1/comments/post/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'DELETE',
      body: JSON.stringify({ password }),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error deleting comment',
          };
        }

        return;
      })
      .then(() => {
        const commentToRemove = _find(comments, { id });
        const newCommentsArray = _without(comments, commentToRemove);

        const sortedComments = newCommentsArray.sort((a, b) => {
          return a.time_stamp - b.time_stamp;
        });

        dispatch(updateComments(sortedComments));
        dispatch(updateIsFetching(false));
      })
      .catch(err => {
        dispatch(showErrorBar(ERROR, err.message));
        dispatch(updateIsFetching(false));

        if (err.type === SERVER_ERROR) {
          return;
        }

        loggerx.error(err, { commentId: id }, { team: 'create' });
      });
  };
}

export function deleteReplyCreator(reply) {
  return (dispatch, getState) => {
    const { password } = getState().share_video;
    const { comments, expanded_ids } = getState().comments;

    return fetch(`/v1/comments/reply/${reply.id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'DELETE',
      body: JSON.stringify({ password }),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error deleting reply',
          };
        }

        return;
      })
      .then(() => {
        const parentComment = {
          ..._find(comments, { id: reply.comment_post_id }),
        };

        const parentCommentIndex = _findIndex(comments, {
          id: parentComment.id,
        });
        const newCommentsArr = comments.slice(0);

        parentComment.replies = _without(parentComment.replies, reply);

        newCommentsArr.splice(parentCommentIndex, 1, parentComment);
        dispatch(updateComments(newCommentsArr));

        if (parentComment.replies.length === 0) {
          dispatch(
            updateExpandedReplyIds(
              _without(expanded_ids, reply.comment_post_id)
            )
          );
        }
      })
      .catch(err => {
        dispatch(showErrorBar(ERROR, err.message));

        if (err.type === SERVER_ERROR) {
          return;
        }

        loggerx.error(
          err,
          { commentId: reply.comment_post_id, replyId: reply.id },
          { team: 'create' }
        );
      });
  };
}

export function fetchCommentsCreator() {
  return (dispatch, getState) => {
    dispatch(fetchComments());

    const {
      video: { id },
      password,
    } = getState().share_video;

    const payload = {
      video_id: id,
      password,
    };

    return fetch('/v1/comments', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error fetching comments',
          };
        }

        return resp.json();
      })
      .then(commentsJSON => {
        dispatch(updateComments(commentsJSON));
      })
      .catch(err => {
        dispatch(updateFetchingCommentsError(err.message));
        dispatch(showErrorBar(ERROR, err.message));

        if (err.type === SERVER_ERROR) {
          return;
        }

        loggerx.error(err, { videoId: payload.video_id }, { team: 'create' });
      });
  };
}
